import { useState, useRef, useEffect } from "react";
import * as React from "react";
import { createPortal } from "react-dom";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as LabelPrimitive from "@radix-ui/react-label";
import {
  Upload,
  X,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { snagCategories, snagSubcategories } from "./snagsData";
import { ProjectApi } from "api/projects/service";
import { useAlert } from "components/UI_components/Alert";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-sm-primary text-primary-foreground hover:bg-sm-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

const Button = React.forwardRef((props, ref) => {
  const { className, variant, size, asChild = false, ...rest } = props;
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...rest}
    />
  );
});
Button.displayName = "Button";

const Input = React.forwardRef((props, ref) => {
  const { className, type, ...rest } = props;
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    />
  );
});
Input.displayName = "Input";

const Textarea = React.forwardRef((props, ref) => {
  const { className, ...rest } = props;
  return (
    <textarea
      ref={ref}
      className={cn(
        "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...rest}
    />
  );
});
Textarea.displayName = "Textarea";

const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
);

const Label = React.forwardRef(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
));
Label.displayName = "Label";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-sm-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline: "text-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

function Badge(props) {
  const { className, variant, ...rest } = props;
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...rest} />
  );
}

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  ),
);
SelectScrollUpButton.displayName = "SelectScrollUpButton";

const SelectScrollDownButton = React.forwardRef(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
  ),
);
SelectScrollDownButton.displayName = "SelectScrollDownButton";

const SelectContent = React.forwardRef(
  ({ className, children, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={6}
        collisionPadding={12}
        className={cn(
          "z-[9999] max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-sm-border bg-background text-popover-foreground shadow-xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = "SelectItem";

const API_BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

const PRIORITY_MAP = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
};

const OTHER_CATEGORY_VALUE = "Other";

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const normalizeProject = (p, i) => ({
  id: String(p?.id ?? p?.project_id ?? p?.site_id ?? i + 1),
  name: p?.name ?? p?.project_name ?? p?.site_name ?? `Project ${i + 1}`,
});

const normalizeMember = (m, i) => ({
  id: String(m?.id ?? m?.user?.id ?? m?.member_id ?? i + 1),
  label:
    `${m?.first_name || m?.user?.first_name || ""} ${m?.last_name || m?.user?.last_name || ""}`.trim() ||
    m?.full_name ||
    m?.name ||
    m?.user?.full_name ||
    m?.user?.name ||
    m?.email ||
    m?.user?.email ||
    `Member ${i + 1}`,
});

async function readErrorMessage(response, fallback) {
  const text = await response.text().catch(() => "");
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return (
      parsed?.detail ||
      parsed?.message ||
      Object.values(parsed || {})?.[0]?.[0] ||
      fallback
    );
  } catch {
    return fallback;
  }
}

async function createSnagAPI({
  orgId,
  siteId,
  token,
  projectId,
  title,
  description,
  remarks,
  issueImage,
  location,
  category,
  subcategory,
  priority,
  expectedClosure,
  status = "Draft",
}) {
  if (!API_BASE_URL) throw new Error("REACT_APP_BASE_URL is missing.");
  if (!token) throw new Error("Auth token is missing.");
  if (!orgId) throw new Error("Organization ID is missing.");

  const resolvedSiteId = siteId || projectId;
  if (!resolvedSiteId) throw new Error("Site ID is missing.");

  const submitCreate = async (statusValue) => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description || remarks || "");
    formData.append("location", location || "");
    formData.append("category", category);
    if (subcategory) formData.append("subcategory", subcategory);
    formData.append("priority", String(PRIORITY_MAP[priority] ?? 2));
    formData.append("status", statusValue);
    if (expectedClosure) {
      formData.append("expected_closure_date", expectedClosure);
    }
    formData.append("site", String(projectId));
    if (projectId && String(projectId) !== String(resolvedSiteId)) {
      formData.append("project", String(projectId));
    }
    if (issueImage) formData.append("issue_image", issueImage);

    const response = await fetch(
      `${API_BASE_URL}/snag-management/org/${orgId}/snags/create/`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      },
    );

    if (!response.ok) {
      const message = await readErrorMessage(
        response,
        `Create snag failed (${response.status})`,
      );
      throw new Error(message);
    }

    return response.json().catch(() => ({}));
  };

  return submitCreate(status);
}

async function assignSnagMembersAPI({ orgId, snagId, token, memberIds }) {
  if (!orgId || !snagId || !token) return;
  if (!Array.isArray(memberIds) || memberIds.length === 0) return;

  const response = await fetch(
    `${API_BASE_URL}/snag-management/org/${orgId}/snags/${snagId}/assign/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        member_ids: memberIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id)),
      }),
    },
  );

  if (!response.ok) {
    const message = await readErrorMessage(
      response,
      `Assign members failed (${response.status})`,
    );
    throw new Error(message);
  }
}

export function CreateSnagDrawer({
  open,
  onOpenChange,
  onSnagCreated,
  token,
  orgId,
  siteId,
}) {
  const { addAlert } = useAlert();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [project, setProject] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [customSubcategory, setCustomSubcategory] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [assignees, setAssignees] = useState([]);
  const [approver, setApprover] = useState("");
  const [expectedClosure, setExpectedClosure] = useState("");
  const [issueImage, setIssueImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const fileInputRef = useRef(null);
  const closeTimerRef = useRef(null);

  const isOtherCategory = category === OTHER_CATEGORY_VALUE;
  const resolvedCategory = isOtherCategory ? customCategory.trim() : category;
  const resolvedSubcategory = isOtherCategory
    ? customSubcategory.trim()
    : subcategory;
  const requiresCustomCategory = isOtherCategory && !resolvedCategory;
  const availableSubcategories =
    snagSubcategories[resolvedCategory] || snagSubcategories[category] || [];

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      setIsClosing(false);
      const raf = window.requestAnimationFrame(() => setIsMounted(true));
      return () => window.cancelAnimationFrame(raf);
    }

    setIsMounted(false);
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !orgId) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      setError(null);

      try {
        const [projectsData, membersRes] = await Promise.all([
          ProjectApi.listSitesByOrganization(orgId),
          token && API_BASE_URL
            ? fetch(`${API_BASE_URL}/org/organization/${orgId}/members/`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              })
            : Promise.resolve(null),
        ]);

        const membersData = membersRes
          ? await membersRes.json().catch(() => ({}))
          : [];
        if (membersRes && !membersRes.ok) {
          throw new Error(
            membersData?.detail ||
              membersData?.message ||
              `Failed to fetch organization members (${membersRes.status})`,
          );
        }

        setProjects(unwrapList(projectsData).map(normalizeProject));
        setMembers(unwrapList(membersData).map(normalizeMember));
      } catch (e) {
        setProjects([]);
        setMembers([]);
        setError(e.message || "Failed to load projects/members");
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [open, orgId, token]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const reset = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setTitle("");
    setDescription("");
    setProject("");
    setLocation("");
    setCategory("");
    setSubcategory("");
    setCustomCategory("");
    setCustomSubcategory("");
    setPriority("Medium");
    setAssignees([]);
    setApprover("");
    setExpectedClosure("");
    setIssueImage(null);
    setImagePreview(null);
    setError(null);
  };

  const requestClose = () => {
    if (isClosing || closeTimerRef.current) return;
    setIsClosing(true);
    setIsMounted(false);
    closeTimerRef.current = window.setTimeout(() => {
      reset();
      onOpenChange(false);
      closeTimerRef.current = null;
    }, 260);
  };

  const handleImageChange = (file) => {
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setIssueImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!resolvedCategory) {
      setError(
        isOtherCategory ? "Please enter a custom category." : "Category is required.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const memberIdsForAssign = approver
        ? Array.from(new Set([...assignees, approver]))
        : assignees;
      const shouldOpen =
        memberIdsForAssign.length > 0 && Boolean(expectedClosure);

      const newSnag = await createSnagAPI({
        orgId,
        siteId,
        token,
        projectId: project,
        title,
        description,
        issueImage,
        location,
        category: resolvedCategory,
        subcategory: resolvedSubcategory,
        priority,
        expectedClosure,
        status: shouldOpen ? "Open" : "Draft",
      });

      const createdSnagId = newSnag?.id || newSnag?.data?.id;

      await assignSnagMembersAPI({
        orgId,
        snagId: createdSnagId,
        token,
        memberIds: memberIdsForAssign,
      });

      onSnagCreated?.(newSnag);
      addAlert("Snag created successfully", "success");
      requestClose();
    } catch (err) {
      addAlert(
        err.message || "Failed to create snag. Please try again.",
        "error",
      );
      setError(err.message || "Failed to create snag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!resolvedCategory) {
      setError(
        isOtherCategory ? "Please enter a custom category." : "Category is required.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newSnag = await createSnagAPI({
        orgId,
        siteId,
        token,
        projectId: project,
        title,
        description,
        issueImage,
        location,
        category: resolvedCategory,
        subcategory: resolvedSubcategory,
        priority,
        expectedClosure,
        status: "Draft",
      });

      onSnagCreated?.(newSnag);
      addAlert("Draft snag created successfully", "success");
      requestClose();
    } catch (err) {
      addAlert(
        err.message || "Failed to save draft snag. Please try again.",
        "error",
      );
      setError(err.message || "Failed to save draft snag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDraftDisabled =
    isClosing ||
    isSubmitting ||
    !title ||
    !project ||
    !resolvedCategory ||
    requiresCustomCategory;
  const isSubmitDisabled =
    isClosing ||
    isSubmitting ||
    !title ||
    !project ||
    !resolvedCategory ||
    requiresCustomCategory ||
    (assignees.length === 0 && !approver) ||
    !expectedClosure;

  if (!open) return null;

  const drawerContent = (
    <div
      className={`fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 transition-opacity duration-300 ${
        isMounted && !isClosing ? "opacity-100" : "opacity-0"
      }`}
      onClick={requestClose}
    >
      <div
        className={`w-full sm:max-w-xl bg-white h-full flex flex-col rounded-l-xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out ${
          isMounted && !isClosing
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex items-center justify-center px-4 h-16 py-2 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-xl font-medium text-[#232323]">
            Create New Snag
          </h3>
          {/* <p className="mt-1 text-sm text-text-gray">SNG-009</p> */}
          <button
            type="button"
            onClick={requestClose}
            className="absolute right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 pb-6">
          <div className="space-y-6">
              <div
                className="cursor-pointer rounded-xl border-2 border-dashed border-sm-border p-4 text-center transition hover:border-primary"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleImageChange(e.dataTransfer.files?.[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageChange(e.target.files?.[0])}
                />
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mx-auto max-h-24 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      className="absolute right-0 top-0 rounded-full bg-dark-red p-1 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (imagePreview) URL.revokeObjectURL(imagePreview);
                        setIssueImage(null);
                        setImagePreview(null);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto mb-1 h-6 w-6 text-text-gray" />
                    <p className="text-sm font-medium text-primary-text">
                      Upload Issue Image
                    </p>
                    <p className="mt-1 text-xs text-text-gray">
                      Drag & drop or click
                    </p>
                  </>
                )}
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium text-[#232323]">
                  Title *
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                />
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium text-[#232323]">
                  Description
                </Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[120px] border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-2 block text-sm font-medium text-[#232323]">
                    Project *
                  </Label>
                  <Select value={project} onValueChange={setProject}>
                    <SelectTrigger className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500">
                      <SelectValue
                        placeholder={
                          loadingOptions
                            ? "Loading projects..."
                            : "Select project"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                      {!loadingOptions && projects.length === 0 && (
                        <SelectItem value="__no_projects__" disabled>
                          No projects found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-medium text-[#232323]">
                    Location
                  </Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-dark-red/20 bg-dark-red/10 px-4 py-3 text-sm text-dark-red">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-2 block text-sm font-medium text-[#232323]">
                    Category *
                  </Label>
                  <Select
                    value={category}
                    onValueChange={(v) => {
                      setCategory(v);
                      setSubcategory("");
                      setCustomCategory("");
                      setCustomSubcategory("");
                    }}
                  >
                    <SelectTrigger className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {snagCategories.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isOtherCategory && (
                    <Input
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                      className="mt-2 h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                    />
                  )}
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-medium text-[#232323]">
                    Subcategory (Optional)
                  </Label>
                  {isOtherCategory ? (
                    <Input
                      value={customSubcategory}
                      onChange={(e) => setCustomSubcategory(e.target.value)}
                      placeholder="Enter custom subcategory"
                      className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                    />
                  ) : (
                    <Select
                      value={subcategory}
                      onValueChange={setSubcategory}
                      disabled={!category}
                    >
                      <SelectTrigger className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500">
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubcategories.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium text-[#232323]">
                  Priority *
                </Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="h-11 border-0 bg-[#F3F4F6] text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["Critical", "High", "Medium", "Low"].map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium text-[#232323]">
                  Assign Members *
                </Label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {members.map((m) => {
                    const active = assignees.includes(m.id);
                    return (
                      <Badge
                        key={m.id}
                        className={cn(
                          "cursor-pointer px-3 py-1.5 text-sm",
                          active
                            ? "bg-sm-primary text-white font-semibold"
                            : "border border-sm-border bg-background text-primary-text hover:bg-task-hover font-normal",
                        )}
                        onClick={() =>
                          setAssignees(
                            active
                              ? assignees.filter((a) => a !== m.id)
                              : [...assignees, m.id],
                          )
                        }
                      >
                        {m.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* <div>
                <Label className="text-sm text-text-gray">
                  Approver / Reviewer
                </Label>
                <Select value={approver} onValueChange={setApprover}>
                  <SelectTrigger className="mt-1 h-11 border-sm-border bg-input-bg">
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.label}
                      </SelectItem>
                    ))}
                    {!loadingOptions && members.length === 0 && (
                      <SelectItem value="__no_members__" disabled>
                        No members found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div> */}
              <div>
                <Label className="mb-2 block text-sm font-medium text-[#232323]">
                  Expected Closure Date *
                </Label>
                <Input
                  type="date"
                  value={expectedClosure}
                  onChange={(e) => setExpectedClosure(e.target.value)}
                  className="h-11 border-0 bg-[#F3F4F6] text-sm text-[#232323] focus-visible:ring-2 focus-visible:ring-purple-500"
                />
              </div>
              {error && (
                <div className="rounded-lg border border-dark-red/20 bg-dark-red/10 px-4 py-3 text-sm text-dark-red">
                  {error}
                </div>
              )}
            </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-white flex shrink-0 flex-col items-center justify-center gap-3 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            className="min-w-[240px] rounded-lg px-6 py-2 text-sm font-medium flex items-center justify-center gap-2 whitespace-nowrap"
            onClick={handleSaveDraft}
            disabled={isDraftDisabled}
          >
            {isSubmitting ? "Saving..." : "Save as Draft"}
          </Button>
          <button
            type="button"
            className={`min-w-[240px] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap ${
              isSubmitDisabled ? "opacity-40" : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isSubmitting ? "Creating..." : "Create & Assign"}
            <span className="rounded-full bg-[#58267F] p-1 flex items-center justify-center">
              <Check size={18} />
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return drawerContent;
  }

  return createPortal(drawerContent, document.body);
}
